import { Course } from '../courses/types';

export const AR_VR_DEVELOPMENT: Course = {
  id: 'ar-vr-development',
  title: 'AR/VR Development',
  description: 'Build immersive augmented and virtual reality experiences using modern XR technologies.',
  level: 'advanced',
  duration: '8-10 weeks',
  estimatedHours: 45,
  tags: ['AR', 'VR', 'XR', 'Unity', 'Oculus', 'Hand Tracking', 'Spatial Computing'],
  prerequisites: ['Unity fundamentals', 'C# programming', '3D mathematics', 'User interface design'],
  learningOutcomes: [
    'Develop VR applications for various headsets',
    'Create AR experiences for mobile and glasses',
    'Implement hand tracking and gesture recognition',
    'Design intuitive spatial user interfaces',
    'Optimize XR applications for performance',
    'Understand XR hardware and platforms'
  ],
  modules: [
    {
      id: 'xr-fundamentals',
      title: 'XR Development Fundamentals',
      description: 'Understand the basics of AR, VR, and mixed reality development.',
      duration: '3 hours',
      difficulty: 'beginner',
      content: `
# XR Development Fundamentals

## Understanding XR Technologies

### XR Spectrum
- **Virtual Reality (VR)**: Fully immersive digital environments
- **Augmented Reality (AR)**: Digital content overlaid on real world
- **Mixed Reality (MR)**: Blend of digital and physical worlds
- **Extended Reality (XR)**: Umbrella term for all immersive technologies

### Key Concepts
- **6 Degrees of Freedom (6DOF)**: Movement in 3D space (x, y, z) plus rotation (pitch, yaw, roll)
- **Tracking**: Monitoring user movement and position
- **Presence**: Sense of being in virtual environment
- **Latency**: Delay between action and visual response
- **Field of View (FOV)**: Extent of observable world

## VR Development Setup

### Unity XR Toolkit
\`\`\`csharp
using UnityEngine;
using UnityEngine.XR;
using UnityEngine.XR.Interaction.Toolkit;

public class VRSetupManager : MonoBehaviour
{
    [Header("VR Components")]
    public XRRig xrRig;
    public XRInteractionManager interactionManager;
    
    [Header("Controllers")]
    public XRController leftController;
    public XRController rightController;
    
    void Start()
    {
        InitializeVR();
    }
    
    void InitializeVR()
    {
        // Check if XR device is present
        if (XRSettings.isDeviceActive)
        {
            Debug.Log($"VR Device: {XRSettings.loadedDeviceName}");
            EnableVRFeatures();
        }
        else
        {
            Debug.LogWarning("No VR device detected");
            EnableDesktopFallback();
        }
    }
    
    void EnableVRFeatures()
    {
        // Enable hand tracking if available
        var handSubsystem = XRGeneralSettings.Instance?.Manager?.activeLoader?.GetLoadedSubsystem<XRHandSubsystem>();
        if (handSubsystem != null)
        {
            Debug.Log("Hand tracking available");
        }
        
        // Configure interaction system
        if (interactionManager != null)
        {
            interactionManager.enabled = true;
        }
    }
    
    void EnableDesktopFallback()
    {
        // Provide desktop alternative for testing
        var simulatedXR = gameObject.AddComponent<DeviceSimulator>();
        simulatedXR.enabled = true;
    }
}
\`\`\`

## AR Development Setup

### ARFoundation Basics
\`\`\`csharp
using UnityEngine;
using UnityEngine.XR.ARFoundation;
using UnityEngine.XR.ARSubsystems;

public class ARSetupManager : MonoBehaviour
{
    [Header("AR Components")]
    public ARSession arSession;
    public ARCamera arCamera;
    public ARPlaneManager planeManager;
    public ARRaycastManager raycastManager;
    public ARTrackedImageManager imageManager;
    
    [Header("UI")]
    public GameObject placementIndicator;
    
    void Start()
    {
        InitializeAR();
    }
    
    void InitializeAR()
    {
        if (ARSession.state == ARSessionState.Unsupported)
        {
            Debug.LogError("AR not supported on this device");
            return;
        }
        
        // Configure plane detection
        if (planeManager != null)
        {
            planeManager.detectionMode = PlaneDetectionMode.Horizontal;
            planeManager.planesChanged += OnPlanesChanged;
        }
        
        // Configure image tracking
        if (imageManager != null)
        {
            imageManager.trackedImagesChanged += OnTrackedImagesChanged;
        }
    }
    
    void OnPlanesChanged(ARPlanesChangedEventArgs eventArgs)
    {
        foreach (var plane in eventArgs.added)
        {
            Debug.Log($"New plane detected: {plane.trackableId}");
        }
    }
    
    void OnTrackedImagesChanged(ARTrackedImagesChangedEventArgs eventArgs)
    {
        foreach (var trackedImage in eventArgs.added)
        {
            Debug.Log($"Image tracked: {trackedImage.referenceImage.name}");
        }
    }
    
    void Update()
    {
        UpdatePlacementIndicator();
    }
    
    void UpdatePlacementIndicator()
    {
        Vector3 screenCenter = Camera.current.ViewportToScreenPoint(new Vector3(0.5f, 0.5f, 0));
        
        List<ARRaycastHit> hits = new List<ARRaycastHit>();
        if (raycastManager.Raycast(screenCenter, hits, TrackableType.PlaneWithinPolygon))
        {
            Pose hitPose = hits[0].pose;
            
            if (placementIndicator != null)
            {
                placementIndicator.SetActive(true);
                placementIndicator.transform.SetPositionAndRotation(hitPose.position, hitPose.rotation);
            }
        }
        else
        {
            if (placementIndicator != null)
            {
                placementIndicator.SetActive(false);
            }
        }
    }
}
\`\`\`

## Platform Differences

### Oculus/Meta Platform
\`\`\`csharp
using UnityEngine;
using Oculus.Interaction;

public class OculusSpecific : MonoBehaviour
{
    [Header("Oculus Features")]
    public OVRPassthroughLayer passthroughLayer;
    public OVRHand leftHand;
    public OVRHand rightHand;
    
    void Start()
    {
        // Enable passthrough for mixed reality
        if (passthroughLayer != null)
        {
            passthroughLayer.projectionSurfaceType = OVRPassthroughLayer.ProjectionSurfaceType.Reconstructed;
        }
        
        // Configure hand tracking
        OVRManager.instance.isInsightPassthroughEnabled = true;
        OVRManager.instance.requestedTrackingOrigin = OVRManager.TrackingOrigin.FloorLevel;
    }
}
\`\`\`

### HTC Vive/SteamVR
\`\`\`csharp
using UnityEngine;
using Valve.VR;

public class SteamVRSpecific : MonoBehaviour
{
    void Start()
    {
        if (SteamVR.initializedState == SteamVR.InitializedStates.InitializeSuccess)
        {
            // SteamVR specific setup
            SteamVR_Settings.instance.lockPhysicsUpdateRateToRenderFrequency = true;
        }
    }
}
\`\`\`

## Performance Considerations

### XR Performance Optimization
\`\`\`csharp
public class XRPerformanceOptimizer : MonoBehaviour
{
    [Header("Performance Settings")]
    public bool enableFixedFoveatedRendering = true;
    public bool enableDynamicResolution = true;
    public int targetFrameRate = 90;
    
    void Start()
    {
        OptimizeForXR();
    }
    
    void OptimizeForXR()
    {
        // Set target frame rate
        Application.targetFrameRate = targetFrameRate;
        
        // Enable foveated rendering if available
        if (enableFixedFoveatedRendering)
        {
            #if UNITY_ANDROID
            // Oculus fixed foveated rendering
            OVRManager.fixedFoveatedRenderingLevel = OVRManager.FixedFoveatedRenderingLevel.Medium;
            #endif
        }
        
        // Configure quality settings for XR
        QualitySettings.vSyncCount = 0; // Disable VSync for XR
        QualitySettings.antiAliasing = 0; // Disable MSAA, use single-pass rendering instead
        
        // Reduce shadow distance
        QualitySettings.shadowDistance = 50f;
        QualitySettings.shadowResolution = ShadowResolution.Low;
    }
}
\`\`\`
      `,
      learningObjectives: [
        'Understand XR technology fundamentals',
        'Set up VR and AR development environments',
        'Configure platform-specific features',
        'Optimize XR applications for performance'
      ]
    },
    {
      id: 'vr-interaction',
      title: 'VR Interaction Systems',
      description: 'Implement natural interaction methods for virtual reality.',
      duration: '4 hours',
      difficulty: 'intermediate',
      content: `
# VR Interaction Systems

## Hand Tracking and Gestures

### Hand Tracking Implementation
\`\`\`csharp
using UnityEngine;
using UnityEngine.XR.Hands;

public class HandTrackingManager : MonoBehaviour
{
    [Header("Hand References")]
    public XRHandSkeletonDriver leftHandSkeleton;
    public XRHandSkeletonDriver rightHandSkeleton;
    
    [Header("Gesture Recognition")]
    public float gestureThreshold = 0.8f;
    
    private XRHandSubsystem handSubsystem;
    
    void Start()
    {
        handSubsystem = XRGeneralSettings.Instance?.Manager?.activeLoader?.GetLoadedSubsystem<XRHandSubsystem>();
        
        if (handSubsystem != null)
        {
            handSubsystem.handsSubsystemUpdated += OnHandsUpdated;
        }
    }
    
    void OnHandsUpdated(XRHandSubsystem subsystem, XRHandSubsystem.UpdateSuccessFlags updateSuccessFlags, XRHandSubsystem.UpdateType updateType)
    {
        if (updateSuccessFlags.HasFlag(XRHandSubsystem.UpdateSuccessFlags.LeftHandRootPose))
        {
            ProcessHandGestures(Handedness.Left);
        }
        
        if (updateSuccessFlags.HasFlag(XRHandSubsystem.UpdateSuccessFlags.RightHandRootPose))
        {
            ProcessHandGestures(Handedness.Right);
        }
    }
    
    void ProcessHandGestures(Handedness handedness)
    {
        // Detect pinch gesture
        if (IsPinching(handedness))
        {
            OnPinchGesture(handedness);
        }
        
        // Detect fist gesture
        if (IsFist(handedness))
        {
            OnFistGesture(handedness);
        }
        
        // Detect pointing gesture
        if (IsPointing(handedness))
        {
            OnPointingGesture(handedness);
        }
    }
    
    bool IsPinching(Handedness handedness)
    {
        // Calculate distance between thumb and index finger tips
        var hand = handedness == Handedness.Left ? leftHandSkeleton : rightHandSkeleton;
        
        if (hand?.jointTransformReferences == null) return false;
        
        var thumbTip = GetJointTransform(hand, XRHandJointID.ThumbTip);
        var indexTip = GetJointTransform(hand, XRHandJointID.IndexTip);
        
        if (thumbTip != null && indexTip != null)
        {
            float distance = Vector3.Distance(thumbTip.position, indexTip.position);
            return distance < 0.03f; // 3cm threshold
        }
        
        return false;
    }
    
    bool IsFist(Handedness handedness)
    {
        var hand = handedness == Handedness.Left ? leftHandSkeleton : rightHandSkeleton;
        if (hand?.jointTransformReferences == null) return false;
        
        // Check if all fingers are curled
        XRHandJointID[] fingerTips = {
            XRHandJointID.IndexTip,
            XRHandJointID.MiddleTip,
            XRHandJointID.RingTip,
            XRHandJointID.LittleTip
        };
        
        var palm = GetJointTransform(hand, XRHandJointID.Palm);
        if (palm == null) return false;
        
        foreach (var fingerTip in fingerTips)
        {
            var tip = GetJointTransform(hand, fingerTip);
            if (tip == null) continue;
            
            float distance = Vector3.Distance(tip.position, palm.position);
            if (distance > 0.08f) // 8cm threshold
            {
                return false;
            }
        }
        
        return true;
    }
    
    Transform GetJointTransform(XRHandSkeletonDriver hand, XRHandJointID jointID)
    {
        if (hand.jointTransformReferences != null && (int)jointID < hand.jointTransformReferences.Length)
        {
            return hand.jointTransformReferences[(int)jointID];
        }
        return null;
    }
    
    void OnPinchGesture(Handedness handedness)
    {
        Debug.Log($"{handedness} hand pinching");
        // Implement pinch interaction logic
    }
    
    void OnFistGesture(Handedness handedness)
    {
        Debug.Log($"{handedness} hand making fist");
        // Implement fist interaction logic
    }
    
    void OnPointingGesture(Handedness handedness)
    {
        Debug.Log($"{handedness} hand pointing");
        // Implement pointing interaction logic
    }
}
\`\`\`

## Controller Input Systems

### Advanced Controller Handling
\`\`\`csharp
using UnityEngine;
using UnityEngine.XR.Interaction.Toolkit;
using UnityEngine.InputSystem;

public class VRControllerManager : MonoBehaviour
{
    [Header("Controller References")]
    public XRController leftController;
    public XRController rightController;
    
    [Header("Haptic Feedback")]
    public float hapticIntensity = 0.5f;
    public float hapticDuration = 0.1f;
    
    [Header("Input Actions")]
    public InputActionReference gripAction;
    public InputActionReference triggerAction;
    public InputActionReference primaryButtonAction;
    public InputActionReference secondaryButtonAction;
    public InputActionReference joystickAction;
    
    void Start()
    {
        SetupInputActions();
    }
    
    void SetupInputActions()
    {
        if (gripAction?.action != null)
        {
            gripAction.action.performed += OnGripPressed;
            gripAction.action.canceled += OnGripReleased;
        }
        
        if (triggerAction?.action != null)
        {
            triggerAction.action.performed += OnTriggerPressed;
            triggerAction.action.canceled += OnTriggerReleased;
        }
        
        if (primaryButtonAction?.action != null)
        {
            primaryButtonAction.action.performed += OnPrimaryButton;
        }
        
        if (joystickAction?.action != null)
        {
            joystickAction.action.performed += OnJoystickMove;
        }
    }
    
    void OnGripPressed(InputAction.CallbackContext context)
    {
        XRController controller = GetControllerFromContext(context);
        if (controller != null)
        {
            SendHapticFeedback(controller, hapticIntensity, hapticDuration);
            Debug.Log($"Grip pressed on {controller.name}");
        }
    }
    
    void OnTriggerPressed(InputAction.CallbackContext context)
    {
        float triggerValue = context.ReadValue<float>();
        XRController controller = GetControllerFromContext(context);
        
        if (controller != null && triggerValue > 0.9f)
        {
            SendHapticFeedback(controller, triggerValue * hapticIntensity, hapticDuration);
            Debug.Log($"Trigger fully pressed on {controller.name}");
        }
    }
    
    void OnJoystickMove(InputAction.CallbackContext context)
    {
        Vector2 joystickValue = context.ReadValue<Vector2>();
        XRController controller = GetControllerFromContext(context);
        
        if (controller != null && joystickValue.magnitude > 0.5f)
        {
            HandleLocomotion(joystickValue, controller);
        }
    }
    
    XRController GetControllerFromContext(InputAction.CallbackContext context)
    {
        // Determine which controller based on input device
        var device = context.control.device;
        
        if (leftController.inputDevice.isValid && leftController.inputDevice == device)
        {
            return leftController;
        }
        else if (rightController.inputDevice.isValid && rightController.inputDevice == device)
        {
            return rightController;
        }
        
        return null;
    }
    
    void SendHapticFeedback(XRController controller, float intensity, float duration)
    {
        if (controller.inputDevice.isValid)
        {
            controller.inputDevice.SendHapticImpulse(0, intensity, duration);
        }
    }
    
    void HandleLocomotion(Vector2 input, XRController controller)
    {
        // Implement smooth locomotion or teleportation
        Transform xrRig = FindObjectOfType<XRRig>()?.transform;
        if (xrRig != null)
        {
            Vector3 forward = Camera.main.transform.forward;
            Vector3 right = Camera.main.transform.right;
            
            forward.y = 0;
            right.y = 0;
            
            Vector3 movement = (forward * input.y + right * input.x) * Time.deltaTime * 3f;
            xrRig.position += movement;
        }
    }
}
\`\`\`

## Spatial UI Design

### 3D User Interface
\`\`\`csharp
using UnityEngine;
using UnityEngine.UI;

public class SpatialUIManager : MonoBehaviour
{
    [Header("UI Components")]
    public Canvas worldSpaceCanvas;
    public float uiDistance = 2f;
    public bool followUser = true;
    
    [Header("Interaction")]
    public LayerMask uiLayerMask = 1 << 5; // UI layer
    public float rayLength = 10f;
    
    private Camera userCamera;
    private LineRenderer pointerLine;
    
    void Start()
    {
        userCamera = Camera.main;
        SetupSpatialUI();
        SetupPointerVisual();
    }
    
    void SetupSpatialUI()
    {
        if (worldSpaceCanvas != null)
        {
            worldSpaceCanvas.renderMode = RenderMode.WorldSpace;
            worldSpaceCanvas.worldCamera = userCamera;
            
            // Set canvas size and position
            RectTransform canvasRect = worldSpaceCanvas.GetComponent<RectTransform>();
            canvasRect.sizeDelta = new Vector2(1920, 1080);
            canvasRect.localScale = Vector3.one * 0.001f; // Scale down for world space
            
            PositionUIInFrontOfUser();
        }
    }
    
    void SetupPointerVisual()
    {
        GameObject pointerObject = new GameObject("UI Pointer");
        pointerLine = pointerObject.AddComponent<LineRenderer>();
        
        pointerLine.material = new Material(Shader.Find("Sprites/Default"));
        pointerLine.color = Color.cyan;
        pointerLine.startWidth = 0.005f;
        pointerLine.endWidth = 0.001f;
        pointerLine.positionCount = 2;
    }
    
    void Update()
    {
        if (followUser)
        {
            PositionUIInFrontOfUser();
        }
        
        HandleUIInteraction();
    }
    
    void PositionUIInFrontOfUser()
    {
        if (worldSpaceCanvas != null && userCamera != null)
        {
            Vector3 forward = userCamera.transform.forward;
            Vector3 targetPosition = userCamera.transform.position + forward * uiDistance;
            
            worldSpaceCanvas.transform.position = targetPosition;
            worldSpaceCanvas.transform.LookAt(userCamera.transform);
            worldSpaceCanvas.transform.Rotate(0, 180, 0); // Face the user
        }
    }
    
    void HandleUIInteraction()
    {
        // Raycast from controller or gaze
        Vector3 rayOrigin = GetInteractionRayOrigin();
        Vector3 rayDirection = GetInteractionRayDirection();
        
        if (Physics.Raycast(rayOrigin, rayDirection, out RaycastHit hit, rayLength, uiLayerMask))
        {
            // Update pointer visual
            UpdatePointerVisual(rayOrigin, hit.point);
            
            // Handle UI interaction
            Button button = hit.collider.GetComponent<Button>();
            if (button != null && IsInteractionTriggered())
            {
                button.onClick.Invoke();
                SendHapticFeedback();
            }
        }
        else
        {
            // No UI hit, hide pointer
            if (pointerLine != null)
            {
                pointerLine.enabled = false;
            }
        }
    }
    
    Vector3 GetInteractionRayOrigin()
    {
        // Use controller position if available, otherwise use camera
        XRController rightController = FindObjectOfType<XRController>();
        if (rightController != null)
        {
            return rightController.transform.position;
        }
        
        return userCamera.transform.position;
    }
    
    Vector3 GetInteractionRayDirection()
    {
        // Use controller forward if available, otherwise use camera forward
        XRController rightController = FindObjectOfType<XRController>();
        if (rightController != null)
        {
            return rightController.transform.forward;
        }
        
        return userCamera.transform.forward;
    }
    
    void UpdatePointerVisual(Vector3 start, Vector3 end)
    {
        if (pointerLine != null)
        {
            pointerLine.enabled = true;
            pointerLine.SetPosition(0, start);
            pointerLine.SetPosition(1, end);
        }
    }
    
    bool IsInteractionTriggered()
    {
        // Check for trigger input or other interaction method
        XRController rightController = FindObjectOfType<XRController>();
        if (rightController != null)
        {
            rightController.inputDevice.TryGetFeatureValue(UnityEngine.XR.CommonUsages.triggerButton, out bool triggerPressed);
            return triggerPressed;
        }
        
        return Input.GetMouseButtonDown(0); // Fallback for desktop testing
    }
    
    void SendHapticFeedback()
    {
        XRController rightController = FindObjectOfType<XRController>();
        if (rightController != null && rightController.inputDevice.isValid)
        {
            rightController.inputDevice.SendHapticImpulse(0, 0.3f, 0.1f);
        }
    }
}
\`\`\`

## Physics-Based Interaction

### Grabbable Objects
\`\`\`csharp
using UnityEngine;
using UnityEngine.XR.Interaction.Toolkit;

public class AdvancedGrabbable : XRGrabInteractable
{
    [Header("Physics Settings")]
    public bool usePhysicsWhenGrabbed = false;
    public float throwForceMultiplier = 1.5f;
    public float angularVelocityMultiplier = 1.0f;
    
    [Header("Snap Settings")]
    public bool useSnapToHand = false;
    public Transform leftHandSnapPoint;
    public Transform rightHandSnapPoint;
    
    private Rigidbody objectRigidbody;
    private Vector3 lastPosition;
    private Vector3 velocity;
    
    protected override void Awake()
    {
        base.Awake();
        objectRigidbody = GetComponent<Rigidbody>();
        lastPosition = transform.position;
    }
    
    protected override void OnSelectEntered(SelectEnterEventArgs args)
    {
        base.OnSelectEntered(args);
        
        if (useSnapToHand)
        {
            SnapToHand(args.interactorObject.transform);
        }
        
        if (usePhysicsWhenGrabbed && objectRigidbody != null)
        {
            objectRigidbody.isKinematic = false;
        }
    }
    
    protected override void OnSelectExited(SelectExitEventArgs args)
    {
        if (objectRigidbody != null)
        {
            // Apply throwing velocity
            Vector3 throwVelocity = velocity * throwForceMultiplier;
            objectRigidbody.velocity = throwVelocity;
            
            // Apply angular velocity for spin
            Vector3 angularVel = CalculateAngularVelocity(args.interactorObject.transform);
            objectRigidbody.angularVelocity = angularVel * angularVelocityMultiplier;
        }
        
        base.OnSelectExited(args);
    }
    
    void SnapToHand(Transform handTransform)
    {
        // Determine which hand is grabbing
        bool isLeftHand = handTransform.name.ToLower().Contains("left");
        Transform snapPoint = isLeftHand ? leftHandSnapPoint : rightHandSnapPoint;
        
        if (snapPoint != null)
        {
            transform.position = handTransform.position;
            transform.rotation = handTransform.rotation * snapPoint.localRotation;
        }
    }
    
    void Update()
    {
        if (isSelected)
        {
            // Calculate velocity for throwing
            velocity = (transform.position - lastPosition) / Time.deltaTime;
        }
        
        lastPosition = transform.position;
    }
    
    Vector3 CalculateAngularVelocity(Transform handTransform)
    {
        // Simple angular velocity calculation based on hand movement
        return handTransform.rotation.eulerAngles * Mathf.Deg2Rad;
    }
}
\`\`\`
      `,
      learningObjectives: [
        'Implement hand tracking and gesture recognition',
        'Create natural controller interactions',
        'Design spatial user interfaces',
        'Build physics-based interaction systems'
      ]
    },
    {
      id: 'ar-applications',
      title: 'AR Application Development',
      description: 'Build practical augmented reality applications and experiences.',
      duration: '4 hours',
      difficulty: 'intermediate',
      content: `
# AR Application Development

## Plane Detection and Tracking

### Advanced Plane Management
\`\`\`csharp
using UnityEngine;
using UnityEngine.XR.ARFoundation;
using UnityEngine.XR.ARSubsystems;
using System.Collections.Generic;

public class ARPlaneController : MonoBehaviour
{
    [Header("Plane Detection")]
    public ARPlaneManager planeManager;
    public PlaneDetectionMode detectionMode = PlaneDetectionMode.Horizontal;
    
    [Header("Visualization")]
    public Material planeMaterial;
    public bool showPlanes = true;
    public float minPlaneSize = 0.25f; // Minimum area in square meters
    
    [Header("Object Placement")]
    public GameObject[] placementPrefabs;
    public LayerMask placementLayerMask = 1;
    
    private Dictionary<TrackableId, ARPlane> detectedPlanes = new Dictionary<TrackableId, ARPlane>();
    private Camera arCamera;
    
    void Start()
    {
        arCamera = Camera.main;
        SetupPlaneDetection();
    }
    
    void SetupPlaneDetection()
    {
        if (planeManager != null)
        {
            planeManager.detectionMode = detectionMode;
            planeManager.planesChanged += OnPlanesChanged;
            
            // Configure plane prefab if needed
            if (planeMaterial != null && planeManager.planePrefab != null)
            {
                var renderer = planeManager.planePrefab.GetComponent<MeshRenderer>();
                if (renderer != null)
                {
                    renderer.material = planeMaterial;
                }
            }
        }
    }
    
    void OnPlanesChanged(ARPlanesChangedEventArgs eventArgs)
    {
        // Handle new planes
        foreach (var plane in eventArgs.added)
        {
            OnPlaneAdded(plane);
        }
        
        // Handle updated planes
        foreach (var plane in eventArgs.updated)
        {
            OnPlaneUpdated(plane);
        }
        
        // Handle removed planes
        foreach (var plane in eventArgs.removed)
        {
            OnPlaneRemoved(plane);
        }
    }
    
    void OnPlaneAdded(ARPlane plane)
    {
        detectedPlanes[plane.trackableId] = plane;
        
        // Check if plane meets minimum size requirement
        if (plane.size.x * plane.size.y >= minPlaneSize)
        {
            Debug.Log($"Suitable plane detected: {plane.trackableId}, Size: {plane.size}");
            
            // Enable plane visualization based on settings
            var meshRenderer = plane.GetComponent<MeshRenderer>();
            if (meshRenderer != null)
            {
                meshRenderer.enabled = showPlanes;
            }
            
            // Add plane interaction
            AddPlaneInteraction(plane);
        }
        else
        {
            // Hide small planes
            plane.gameObject.SetActive(false);
        }
    }
    
    void OnPlaneUpdated(ARPlane plane)
    {
        // Re-evaluate plane suitability after update
        bool isSuitable = plane.size.x * plane.size.y >= minPlaneSize;
        plane.gameObject.SetActive(isSuitable);
        
        if (isSuitable)
        {
            var meshRenderer = plane.GetComponent<MeshRenderer>();
            if (meshRenderer != null)
            {
                meshRenderer.enabled = showPlanes;
            }
        }
    }
    
    void OnPlaneRemoved(ARPlane plane)
    {
        detectedPlanes.Remove(plane.trackableId);
        Debug.Log($"Plane removed: {plane.trackableId}");
    }
    
    void AddPlaneInteraction(ARPlane plane)
    {
        // Add touch interaction to plane
        var planeInteraction = plane.gameObject.GetComponent<PlaneInteraction>();
        if (planeInteraction == null)
        {
            planeInteraction = plane.gameObject.AddComponent<PlaneInteraction>();
            planeInteraction.Initialize(this);
        }
    }
    
    public void PlaceObjectOnPlane(Vector3 hitPoint, ARPlane plane)
    {
        if (placementPrefabs.Length > 0)
        {
            GameObject prefab = placementPrefabs[Random.Range(0, placementPrefabs.Length)];
            GameObject placedObject = Instantiate(prefab, hitPoint, Quaternion.identity);
            
            // Align with plane
            placedObject.transform.up = plane.normal;
            
            // Add placed object behavior
            var placedBehavior = placedObject.GetComponent<ARPlacedObject>();
            if (placedBehavior == null)
            {
                placedBehavior = placedObject.AddComponent<ARPlacedObject>();
            }
            
            placedBehavior.Initialize(plane);
        }
    }
    
    public void TogglePlaneVisibility()
    {
        showPlanes = !showPlanes;
        
        foreach (var plane in detectedPlanes.Values)
        {
            var meshRenderer = plane.GetComponent<MeshRenderer>();
            if (meshRenderer != null)
            {
                meshRenderer.enabled = showPlanes;
            }
        }
    }
    
    public ARPlane GetBestPlane(Vector3 preferredPosition)
    {
        ARPlane bestPlane = null;
        float closestDistance = float.MaxValue;
        
        foreach (var plane in detectedPlanes.Values)
        {
            if (plane.size.x * plane.size.y >= minPlaneSize)
            {
                float distance = Vector3.Distance(plane.center, preferredPosition);
                if (distance < closestDistance)
                {
                    closestDistance = distance;
                    bestPlane = plane;
                }
            }
        }
        
        return bestPlane;
    }
}

// Helper class for plane interaction
public class PlaneInteraction : MonoBehaviour
{
    private ARPlaneController controller;
    private ARPlane plane;
    
    public void Initialize(ARPlaneController planeController)
    {
        controller = planeController;
        plane = GetComponent<ARPlane>();
    }
    
    void Update()
    {
        HandleTouchInput();
    }
    
    void HandleTouchInput()
    {
        if (Input.touchCount > 0)
        {
            Touch touch = Input.GetTouch(0);
            
            if (touch.phase == TouchPhase.Began)
            {
                Vector3 touchPosition = touch.position;
                
                // Raycast from touch position
                Ray ray = Camera.main.ScreenPointToRay(touchPosition);
                
                if (Physics.Raycast(ray, out RaycastHit hit))
                {
                    if (hit.collider.gameObject == gameObject)
                    {
                        controller.PlaceObjectOnPlane(hit.point, plane);
                    }
                }
            }
        }
    }
}
\`\`\`

## Image Tracking and Recognition

### Marker-Based AR
\`\`\`csharp
using UnityEngine;
using UnityEngine.XR.ARFoundation;
using UnityEngine.XR.ARSubsystems;
using System.Collections.Generic;

public class ARImageTracker : MonoBehaviour
{
    [Header("Image Tracking")]
    public ARTrackedImageManager trackedImageManager;
    public XRReferenceImageLibrary imageLibrary;
    
    [Header("Tracking Responses")]
    public GameObject[] trackingPrefabs;
    
    private Dictionary<string, GameObject> spawnedObjects = new Dictionary<string, GameObject>();
    private Dictionary<string, TrackingState> trackingStates = new Dictionary<string, TrackingState>();
    
    void Start()
    {
        SetupImageTracking();
    }
    
    void SetupImageTracking()
    {
        if (trackedImageManager != null)
        {
            trackedImageManager.trackedImagesChanged += OnTrackedImagesChanged;
            
            if (imageLibrary != null)
            {
                trackedImageManager.referenceLibrary = imageLibrary;
            }
        }
    }
    
    void OnTrackedImagesChanged(ARTrackedImagesChangedEventArgs eventArgs)
    {
        // Handle newly detected images
        foreach (var trackedImage in eventArgs.added)
        {
            OnImageDetected(trackedImage);
        }
        
        // Handle updated tracking
        foreach (var trackedImage in eventArgs.updated)
        {
            OnImageUpdated(trackedImage);
        }
        
        // Handle lost tracking
        foreach (var trackedImage in eventArgs.removed)
        {
            OnImageLost(trackedImage);
        }
    }
    
    void OnImageDetected(ARTrackedImage trackedImage)
    {
        string imageName = trackedImage.referenceImage.name;
        Debug.Log($"Image detected: {imageName}");
        
        // Create AR content for this image
        GameObject arContent = CreateARContentForImage(imageName, trackedImage.transform);
        
        if (arContent != null)
        {
            spawnedObjects[imageName] = arContent;
            trackingStates[imageName] = trackedImage.trackingState;
            
            // Position content relative to tracked image
            arContent.transform.SetParent(trackedImage.transform);
            arContent.transform.localPosition = Vector3.zero;
            arContent.transform.localRotation = Quaternion.identity;
            
            // Scale content based on image size
            Vector2 imageSize = trackedImage.size;
            float scaleFactor = Mathf.Max(imageSize.x, imageSize.y);
            arContent.transform.localScale = Vector3.one * scaleFactor;
        }
    }
    
    void OnImageUpdated(ARTrackedImage trackedImage)
    {
        string imageName = trackedImage.referenceImage.name;
        
        if (spawnedObjects.ContainsKey(imageName))
        {
            GameObject arContent = spawnedObjects[imageName];
            TrackingState previousState = trackingStates.GetValueOrDefault(imageName, TrackingState.None);
            
            // Update tracking state
            trackingStates[imageName] = trackedImage.trackingState;
            
            // Handle tracking state changes
            switch (trackedImage.trackingState)
            {
                case TrackingState.Tracking:
                    if (previousState != TrackingState.Tracking)
                    {
                        OnImageTrackingRestored(arContent, trackedImage);
                    }
                    break;
                    
                case TrackingState.Limited:
                    OnImageTrackingLimited(arContent, trackedImage);
                    break;
                    
                case TrackingState.None:
                    OnImageTrackingLost(arContent, trackedImage);
                    break;
            }
        }
    }
    
    void OnImageLost(ARTrackedImage trackedImage)
    {
        string imageName = trackedImage.referenceImage.name;
        
        if (spawnedObjects.ContainsKey(imageName))
        {
            GameObject arContent = spawnedObjects[imageName];
            OnImageCompletelyLost(arContent, trackedImage);
            
            // Optionally destroy content or keep it for potential re-tracking
            // Destroy(arContent);
            // spawnedObjects.Remove(imageName);
        }
    }
    
    GameObject CreateARContentForImage(string imageName, Transform parent)
    {
        // Find appropriate prefab for this image
        GameObject prefab = GetPrefabForImage(imageName);
        
        if (prefab != null)
        {
            GameObject instance = Instantiate(prefab);
            
            // Add image-specific behavior
            var imageContent = instance.GetComponent<ARImageContent>();
            if (imageContent == null)
            {
                imageContent = instance.AddComponent<ARImageContent>();
            }
            
            imageContent.Initialize(imageName, this);
            
            return instance;
        }
        
        return null;
    }
    
    GameObject GetPrefabForImage(string imageName)
    {
        // Map image names to prefabs
        switch (imageName.ToLower())
        {
            case "logo":
                return trackingPrefabs.Length > 0 ? trackingPrefabs[0] : null;
            case "poster":
                return trackingPrefabs.Length > 1 ? trackingPrefabs[1] : null;
            case "product":
                return trackingPrefabs.Length > 2 ? trackingPrefabs[2] : null;
            default:
                return trackingPrefabs.Length > 0 ? trackingPrefabs[0] : null;
        }
    }
    
    void OnImageTrackingRestored(GameObject arContent, ARTrackedImage trackedImage)
    {
        arContent.SetActive(true);
        Debug.Log($"Tracking restored for: {trackedImage.referenceImage.name}");
    }
    
    void OnImageTrackingLimited(GameObject arContent, ARTrackedImage trackedImage)
    {
        // Reduce content fidelity or show warning
        var renderer = arContent.GetComponent<Renderer>();
        if (renderer != null)
        {
            renderer.material.color = Color.yellow; // Visual indicator
        }
    }
    
    void OnImageTrackingLost(GameObject arContent, ARTrackedImage trackedImage)
    {
        // Hide content or show placeholder
        arContent.SetActive(false);
        Debug.Log($"Tracking lost for: {trackedImage.referenceImage.name}");
    }
    
    void OnImageCompletelyLost(GameObject arContent, ARTrackedImage trackedImage)
    {
        Debug.Log($"Image completely lost: {trackedImage.referenceImage.name}");
        // Clean up or prepare for potential re-detection
    }
}

// Component for AR content attached to tracked images
public class ARImageContent : MonoBehaviour
{
    private string associatedImageName;
    private ARImageTracker tracker;
    private bool isAnimating = false;
    
    public void Initialize(string imageName, ARImageTracker imageTracker)
    {
        associatedImageName = imageName;
        tracker = imageTracker;
        
        StartIntroAnimation();
    }
    
    void StartIntroAnimation()
    {
        if (!isAnimating)
        {
            isAnimating = true;
            
            // Scale in animation
            transform.localScale = Vector3.zero;
            LeanTween.scale(gameObject, Vector3.one, 0.5f)
                .setEaseOutBack()
                .setOnComplete(() => isAnimating = false);
        }
    }
    
    public void OnTrackingLost()
    {
        // Fade out or scale down
        LeanTween.alpha(gameObject, 0.3f, 0.2f);
    }
    
    public void OnTrackingRestored()
    {
        // Fade back in
        LeanTween.alpha(gameObject, 1.0f, 0.2f);
    }
}
\`\`\`

## Occlusion and Lighting

### Environmental Understanding
\`\`\`csharp
using UnityEngine;
using UnityEngine.XR.ARFoundation;
using UnityEngine.Rendering;

public class AREnvironmentManager : MonoBehaviour
{
    [Header("Occlusion")]
    public AROcclusionManager occlusionManager;
    public bool enableOcclusion = true;
    
    [Header("Lighting")]
    public ARCameraManager cameraManager;
    public Light directionalLight;
    public bool adaptLighting = true;
    
    [Header("Environment Probes")]
    public AREnvironmentProbeManager probeManager;
    public bool useEnvironmentProbes = true;
    
    void Start()
    {
        SetupOcclusion();
        SetupLighting();
        SetupEnvironmentProbes();
    }
    
    void SetupOcclusion()
    {
        if (occlusionManager != null)
        {
            occlusionManager.enabled = enableOcclusion;
            
            if (enableOcclusion)
            {
                // Configure occlusion settings
                occlusionManager.requestedHumanDepthMode = HumanDepthMode.Best;
                occlusionManager.requestedHumanStencilMode = HumanStencilMode.Best;
                occlusionManager.requestedEnvironmentDepthMode = EnvironmentDepthMode.Best;
            }
        }
    }
    
    void SetupLighting()
    {
        if (cameraManager != null && adaptLighting)
        {
            cameraManager.frameReceived += OnCameraFrameReceived;
        }
    }
    
    void SetupEnvironmentProbes()
    {
        if (probeManager != null)
        {
            probeManager.enabled = useEnvironmentProbes;
            
            if (useEnvironmentProbes)
            {
                probeManager.environmentProbesChanged += OnEnvironmentProbesChanged;
            }
        }
    }
    
    void OnCameraFrameReceived(ARCameraFrameEventArgs eventArgs)
    {
        // Update lighting based on camera frame
        if (eventArgs.lightEstimation.averageBrightness.HasValue)
        {
            UpdateLightingBrightness(eventArgs.lightEstimation.averageBrightness.Value);
        }
        
        if (eventArgs.lightEstimation.averageColorTemperature.HasValue)
        {
            UpdateLightingTemperature(eventArgs.lightEstimation.averageColorTemperature.Value);
        }
        
        if (eventArgs.lightEstimation.colorCorrection.HasValue)
        {
            UpdateColorCorrection(eventArgs.lightEstimation.colorCorrection.Value);
        }
    }
    
    void UpdateLightingBrightness(float brightness)
    {
        if (directionalLight != null)
        {
            // Map brightness to light intensity
            float targetIntensity = Mathf.Clamp(brightness * 2f, 0.1f, 3f);
            directionalLight.intensity = targetIntensity;
        }
    }
    
    void UpdateLightingTemperature(float temperature)
    {
        if (directionalLight != null)
        {
            // Convert temperature to color
            Color temperatureColor = Mathf.CorrelatedColorTemperatureToRGB(temperature);
            directionalLight.color = temperatureColor;
        }
    }
    
    void UpdateColorCorrection(Color colorCorrection)
    {
        // Apply color correction to environment lighting
        RenderSettings.ambientLight = colorCorrection;
    }
    
    void OnEnvironmentProbesChanged(AREnvironmentProbesChangedEventArgs eventArgs)
    {
        foreach (var probe in eventArgs.added)
        {
            Debug.Log($"Environment probe added: {probe.trackableId}");
            SetupEnvironmentProbe(probe);
        }
        
        foreach (var probe in eventArgs.updated)
        {
            UpdateEnvironmentProbe(probe);
        }
        
        foreach (var probe in eventArgs.removed)
        {
            Debug.Log($"Environment probe removed: {probe.trackableId}");
        }
    }
    
    void SetupEnvironmentProbe(AREnvironmentProbe probe)
    {
        // Configure the environment probe
        var reflectionProbe = probe.GetComponent<ReflectionProbe>();
        if (reflectionProbe != null)
        {
            reflectionProbe.mode = ReflectionProbeMode.Realtime;
            reflectionProbe.refreshMode = ReflectionProbeRefreshMode.ViaScripting;
            reflectionProbe.timeSlicingMode = ReflectionProbeTimeSlicingMode.AllFacesAtOnce;
        }
    }
    
    void UpdateEnvironmentProbe(AREnvironmentProbe probe)
    {
        // Update probe when tracking changes
        var reflectionProbe = probe.GetComponent<ReflectionProbe>();
        if (reflectionProbe != null && probe.trackingState == TrackingState.Tracking)
        {
            reflectionProbe.RenderProbe();
        }
    }
    
    public void ToggleOcclusion()
    {
        enableOcclusion = !enableOcclusion;
        
        if (occlusionManager != null)
        {
            occlusionManager.enabled = enableOcclusion;
        }
    }
    
    public void ToggleLightingAdaptation()
    {
        adaptLighting = !adaptLighting;
        
        if (!adaptLighting && directionalLight != null)
        {
            // Reset to default lighting
            directionalLight.intensity = 1f;
            directionalLight.color = Color.white;
        }
    }
}
\`\`\`
      `,
      learningObjectives: [
        'Implement robust plane detection systems',
        'Create marker-based AR applications',
        'Handle occlusion and environmental lighting',
        'Build practical AR user experiences'
      ]
    },
    {
      id: 'xr-optimization',
      title: 'XR Performance Optimization',
      description: 'Optimize AR/VR applications for smooth performance across devices.',
      duration: '3 hours',
      difficulty: 'advanced',
      content: `
# XR Performance Optimization

## Frame Rate and Latency Optimization

### Performance Monitoring System
\`\`\`csharp
using UnityEngine;
using System.Collections.Generic;
using UnityEngine.XR;

public class XRPerformanceMonitor : MonoBehaviour
{
    [Header("Monitoring Settings")]
    public bool enableProfiling = true;
    public float samplingInterval = 1f;
    public int maxSamples = 60;
    
    [Header("Performance Targets")]
    public float targetFrameRate = 90f;
    public float maxFrameTime = 11.1f; // ms for 90fps
    public float warnFrameTime = 13.3f; // ms for 75fps
    
    [Header("Debug Display")]
    public bool showDebugUI = true;
    public GUIStyle debugStyle;
    
    private Queue<float> frameTimeHistory = new Queue<float>();
    private Queue<float> cpuTimeHistory = new Queue<float>();
    private Queue<float> gpuTimeHistory = new Queue<float>();
    
    private float lastSampleTime;
    private float averageFrameTime;
    private float averageCpuTime;
    private float averageGpuTime;
    
    // Performance state
    private PerformanceLevel currentPerformanceLevel = PerformanceLevel.High;
    private bool isThrottling = false;
    
    public enum PerformanceLevel
    {
        Low,
        Medium,
        High,
        Ultra
    }
    
    void Start()
    {
        InitializeMonitoring();
    }
    
    void InitializeMonitoring()
    {
        Application.targetFrameRate = (int)targetFrameRate;
        
        // Initialize debug style
        if (debugStyle == null)
        {
            debugStyle = new GUIStyle();
            debugStyle.fontSize = 24;
            debugStyle.normal.textColor = Color.white;
        }
        
        InvokeRepeating(nameof(SamplePerformance), 0f, samplingInterval);
    }
    
    void Update()
    {
        // Calculate frame time
        float frameTime = Time.unscaledDeltaTime * 1000f; // Convert to milliseconds
        
        frameTimeHistory.Enqueue(frameTime);
        if (frameTimeHistory.Count > maxSamples)
        {
            frameTimeHistory.Dequeue();
        }
        
        // Check for performance issues
        if (frameTime > warnFrameTime)
        {
            OnPerformanceWarning(frameTime);
        }
        
        if (frameTime > maxFrameTime)
        {
            OnPerformanceCritical(frameTime);
        }
    }
    
    void SamplePerformance()
    {
        if (!enableProfiling) return;
        
        // Calculate averages
        CalculateAverages();
        
        // Adjust performance level if needed
        AdjustPerformanceLevel();
        
        // Log performance data
        if (enableProfiling)
        {
            LogPerformanceData();
        }
    }
    
    void CalculateAverages()
    {
        // Calculate average frame time
        float frameTimeSum = 0f;
        foreach (float frameTime in frameTimeHistory)
        {
            frameTimeSum += frameTime;
        }
        averageFrameTime = frameTimeSum / frameTimeHistory.Count;
        
        // Calculate CPU and GPU times (simplified)
        averageCpuTime = averageFrameTime * 0.7f; // Rough estimate
        averageGpuTime = averageFrameTime * 0.3f; // Rough estimate
    }
    
    void AdjustPerformanceLevel()
    {
        PerformanceLevel targetLevel = currentPerformanceLevel;
        
        if (averageFrameTime > maxFrameTime * 1.2f)
        {
            // Performance too low, reduce quality
            targetLevel = (PerformanceLevel)Mathf.Max((int)currentPerformanceLevel - 1, 0);
        }
        else if (averageFrameTime < maxFrameTime * 0.8f)
        {
            // Performance good, can increase quality
            targetLevel = (PerformanceLevel)Mathf.Min((int)currentPerformanceLevel + 1, 3);
        }
        
        if (targetLevel != currentPerformanceLevel)
        {
            SetPerformanceLevel(targetLevel);
        }
    }
    
    void SetPerformanceLevel(PerformanceLevel level)
    {
        currentPerformanceLevel = level;
        
        switch (level)
        {
            case PerformanceLevel.Low:
                ApplyLowQualitySettings();
                break;
            case PerformanceLevel.Medium:
                ApplyMediumQualitySettings();
                break;
            case PerformanceLevel.High:
                ApplyHighQualitySettings();
                break;
            case PerformanceLevel.Ultra:
                ApplyUltraQualitySettings();
                break;
        }
        
        Debug.Log($"Performance level changed to: {level}");
    }
    
    void ApplyLowQualitySettings()
    {
        QualitySettings.SetQualityLevel(0);
        QualitySettings.shadowDistance = 25f;
        QualitySettings.shadowResolution = ShadowResolution.Low;
        QualitySettings.pixelLightCount = 1;
        
        // Reduce render scale for VR
        #if UNITY_XR_ENABLED
        XRSettings.renderViewportScale = 0.7f;
        #endif
    }
    
    void ApplyMediumQualitySettings()
    {
        QualitySettings.SetQualityLevel(1);
        QualitySettings.shadowDistance = 50f;
        QualitySettings.shadowResolution = ShadowResolution.Medium;
        QualitySettings.pixelLightCount = 2;
        
        #if UNITY_XR_ENABLED
        XRSettings.renderViewportScale = 0.85f;
        #endif
    }
    
    void ApplyHighQualitySettings()
    {
        QualitySettings.SetQualityLevel(2);
        QualitySettings.shadowDistance = 75f;
        QualitySettings.shadowResolution = ShadowResolution.High;
        QualitySettings.pixelLightCount = 4;
        
        #if UNITY_XR_ENABLED
        XRSettings.renderViewportScale = 1.0f;
        #endif
    }
    
    void ApplyUltraQualitySettings()
    {
        QualitySettings.SetQualityLevel(3);
        QualitySettings.shadowDistance = 100f;
        QualitySettings.shadowResolution = ShadowResolution.VeryHigh;
        QualitySettings.pixelLightCount = 8;
        
        #if UNITY_XR_ENABLED
        XRSettings.renderViewportScale = 1.2f;
        #endif
    }
    
    void OnPerformanceWarning(float frameTime)
    {
        if (!isThrottling)
        {
            Debug.LogWarning($"Performance warning: Frame time {frameTime:F1}ms exceeds target");
        }
    }
    
    void OnPerformanceCritical(float frameTime)
    {
        if (!isThrottling)
        {
            Debug.LogError($"Critical performance issue: Frame time {frameTime:F1}ms");
            isThrottling = true;
            
            // Immediate quality reduction
            SetPerformanceLevel(PerformanceLevel.Low);
            
            // Reset throttling flag after a delay
            Invoke(nameof(ResetThrottling), 2f);
        }
    }
    
    void ResetThrottling()
    {
        isThrottling = false;
    }
    
    void LogPerformanceData()
    {
        float currentFPS = 1000f / averageFrameTime;
        Debug.Log($"Performance: {currentFPS:F1} FPS, Frame: {averageFrameTime:F1}ms, CPU: {averageCpuTime:F1}ms, GPU: {averageGpuTime:F1}ms");
    }
    
    void OnGUI()
    {
        if (!showDebugUI) return;
        
        float currentFPS = 1000f / averageFrameTime;
        
        string debugText = $"FPS: {currentFPS:F1}\n";
        debugText += $"Frame: {averageFrameTime:F1}ms\n";
        debugText += $"Level: {currentPerformanceLevel}\n";
        debugText += $"XR Device: {XRSettings.loadedDeviceName}";
        
        // Color based on performance
        if (averageFrameTime > maxFrameTime)
        {
            debugStyle.normal.textColor = Color.red;
        }
        else if (averageFrameTime > warnFrameTime)
        {
            debugStyle.normal.textColor = Color.yellow;
        }
        else
        {
            debugStyle.normal.textColor = Color.green;
        }
        
        GUI.Label(new Rect(10, 10, 300, 200), debugText, debugStyle);
    }
}
\`\`\`

## LOD and Culling Systems

### Adaptive Level of Detail
\`\`\`csharp
using UnityEngine;
using System.Collections.Generic;

public class XRLODManager : MonoBehaviour
{
    [Header("LOD Settings")]
    public Camera xrCamera;
    public float[] lodDistances = { 10f, 25f, 50f, 100f };
    public float updateInterval = 0.1f;
    
    [Header("Culling")]
    public bool enableFrustumCulling = true;
    public bool enableOcclusionCulling = true;
    public LayerMask cullingMask = -1;
    
    private List<XRLODObject> lodObjects = new List<XRLODObject>();
    private float lastUpdateTime;
    
    void Start()
    {
        if (xrCamera == null)
        {
            xrCamera = Camera.main;
        }
        
        // Find all LOD objects in the scene
        FindLODObjects();
    }
    
    void FindLODObjects()
    {
        XRLODObject[] foundObjects = FindObjectsOfType<XRLODObject>();
        lodObjects.AddRange(foundObjects);
        
        Debug.Log($"Found {lodObjects.Count} LOD objects to manage");
    }
    
    void Update()
    {
        if (Time.time - lastUpdateTime >= updateInterval)
        {
            UpdateLODs();
            lastUpdateTime = Time.time;
        }
    }
    
    void UpdateLODs()
    {
        Vector3 cameraPosition = xrCamera.transform.position;
        Plane[] cameraFrustum = enableFrustumCulling ? GeometryUtility.CalculateFrustumPlanes(xrCamera) : null;
        
        foreach (var lodObject in lodObjects)
        {
            if (lodObject == null) continue;
            
            // Calculate distance to camera
            float distance = Vector3.Distance(cameraPosition, lodObject.transform.position);
            
            // Check frustum culling
            bool inFrustum = true;
            if (enableFrustumCulling && cameraFrustum != null)
            {
                inFrustum = GeometryUtility.TestPlanesAABB(cameraFrustum, lodObject.GetBounds());
            }
            
            // Update LOD level
            if (inFrustum)
            {
                int lodLevel = CalculateLODLevel(distance);
                lodObject.SetLODLevel(lodLevel);
            }
            else
            {
                // Cull object
                lodObject.SetCulled(true);
            }
        }
    }
    
    int CalculateLODLevel(float distance)
    {
        for (int i = 0; i < lodDistances.Length; i++)
        {
            if (distance <= lodDistances[i])
            {
                return i;
            }
        }
        
        return lodDistances.Length; // Highest LOD level (or culled)
    }
    
    public void RegisterLODObject(XRLODObject lodObject)
    {
        if (!lodObjects.Contains(lodObject))
        {
            lodObjects.Add(lodObject);
        }
    }
    
    public void UnregisterLODObject(XRLODObject lodObject)
    {
        lodObjects.Remove(lodObject);
    }
}

// Component for objects that support LOD
public class XRLODObject : MonoBehaviour
{
    [Header("LOD Meshes")]
    public GameObject[] lodMeshes;
    
    [Header("Settings")]
    public bool useDistanceCulling = true;
    public float maxDistance = 150f;
    
    private int currentLODLevel = -1;
    private bool isCulled = false;
    private Bounds objectBounds;
    
    void Start()
    {
        // Calculate bounds
        CalculateBounds();
        
        // Register with LOD manager
        XRLODManager lodManager = FindObjectOfType<XRLODManager>();
        if (lodManager != null)
        {
            lodManager.RegisterLODObject(this);
        }
        
        // Initialize with highest detail
        SetLODLevel(0);
    }
    
    void CalculateBounds()
    {
        Renderer[] renderers = GetComponentsInChildren<Renderer>();
        if (renderers.Length > 0)
        {
            objectBounds = renderers[0].bounds;
            foreach (var renderer in renderers)
            {
                objectBounds.Encapsulate(renderer.bounds);
            }
        }
        else
        {
            objectBounds = new Bounds(transform.position, Vector3.one);
        }
    }
    
    public void SetLODLevel(int level)
    {
        if (level == currentLODLevel || level >= lodMeshes.Length)
            return;
        
        // Disable current LOD
        if (currentLODLevel >= 0 && currentLODLevel < lodMeshes.Length)
        {
            lodMeshes[currentLODLevel].SetActive(false);
        }
        
        // Enable new LOD
        if (level >= 0 && level < lodMeshes.Length)
        {
            lodMeshes[level].SetActive(true);
            currentLODLevel = level;
            isCulled = false;
        }
        else
        {
            // Cull object
            SetCulled(true);
        }
    }
    
    public void SetCulled(bool culled)
    {
        if (isCulled == culled) return;
        
        isCulled = culled;
        
        if (culled)
        {
            // Disable all LODs
            foreach (var mesh in lodMeshes)
            {
                mesh.SetActive(false);
            }
        }
        else
        {
            // Re-enable current LOD
            if (currentLODLevel >= 0 && currentLODLevel < lodMeshes.Length)
            {
                lodMeshes[currentLODLevel].SetActive(true);
            }
        }
    }
    
    public Bounds GetBounds()
    {
        return objectBounds;
    }
    
    void OnDestroy()
    {
        // Unregister from LOD manager
        XRLODManager lodManager = FindObjectOfType<XRLODManager>();
        if (lodManager != null)
        {
            lodManager.UnregisterLODObject(this);
        }
    }
}
\`\`\`

## Shader and Rendering Optimization

### XR-Optimized Shaders
\`\`\`hlsl
// XR Optimized Standard Shader
Shader "XR/OptimizedStandard"
{
    Properties
    {
        _MainTex ("Albedo", 2D) = "white" {}
        _Color ("Color", Color) = (1,1,1,1)
        _Metallic ("Metallic", Range(0,1)) = 0.0
        _Smoothness ("Smoothness", Range(0,1)) = 0.5
        _OcclusionStrength ("Occlusion", Range(0,1)) = 1.0
        _EmissionColor ("Emission", Color) = (0,0,0)
        
        // XR specific properties
        _LODFade ("LOD Fade", Range(0,1)) = 1.0
        [Toggle] _UseSimpleLighting ("Use Simple Lighting", Float) = 0
    }
    
    SubShader
    {
        Tags { "RenderType"="Opaque" }
        LOD 200
        
        CGPROGRAM
        #pragma surface surf Standard fullforwardshadows
        #pragma target 3.0
        
        // XR optimizations
        #pragma multi_compile _ XR_SIMPLE_LIGHTING
        #pragma multi_compile _ LOD_FADE_CROSSFADE
        
        sampler2D _MainTex;
        
        struct Input
        {
            float2 uv_MainTex;
            #ifdef LOD_FADE_CROSSFADE
                float4 screenPos;
            #endif
        };
        
        half _Metallic;
        half _Smoothness;
        half _OcclusionStrength;
        fixed4 _Color;
        fixed4 _EmissionColor;
        half _LODFade;
        float _UseSimpleLighting;
        
        void surf (Input IN, inout SurfaceOutputStandard o)
        {
            // Handle LOD crossfading
            #ifdef LOD_FADE_CROSSFADE
                float2 vpos = IN.screenPos.xy / IN.screenPos.w * _ScreenParams.xy;
                UnityApplyDitherCrossFade(vpos);
            #endif
            
            // Sample albedo
            fixed4 c = tex2D(_MainTex, IN.uv_MainTex) * _Color;
            o.Albedo = c.rgb;
            
            // Simplified lighting for distant objects
            if (_UseSimpleLighting > 0.5)
            {
                o.Metallic = 0;
                o.Smoothness = 0.1;
                o.Occlusion = 1;
                o.Emission = c.rgb * 0.1; // Fake ambient
            }
            else
            {
                o.Metallic = _Metallic;
                o.Smoothness = _Smoothness;
                o.Occlusion = _OcclusionStrength;
                o.Emission = _EmissionColor;
            }
            
            o.Alpha = c.a * _LODFade;
        }
        ENDCG
    }
    
    // Fallback for lower-end devices
    FallBack "Mobile/Diffuse"
}
\`\`\`

### Shader Optimization Manager
\`\`\`csharp
using UnityEngine;
using System.Collections.Generic;

public class XRShaderOptimizer : MonoBehaviour
{
    [Header("Optimization Settings")]
    public bool enableDynamicOptimization = true;
    public float optimizationRadius = 30f;
    public float updateInterval = 0.2f;
    
    [Header("Performance Thresholds")]
    public float highPerformanceThreshold = 16.6f; // 60fps
    public float lowPerformanceThreshold = 22.2f;  // 45fps
    
    private Camera xrCamera;
    private List<OptimizedRenderer> managedRenderers = new List<OptimizedRenderer>();
    private float lastOptimizationTime;
    
    void Start()
    {
        xrCamera = Camera.main;
        FindOptimizedRenderers();
    }
    
    void FindOptimizedRenderers()
    {
        OptimizedRenderer[] renderers = FindObjectsOfType<OptimizedRenderer>();
        managedRenderers.AddRange(renderers);
        
        Debug.Log($"Managing {managedRenderers.Count} optimized renderers");
    }
    
    void Update()
    {
        if (enableDynamicOptimization && Time.time - lastOptimizationTime >= updateInterval)
        {
            OptimizeRenderers();
            lastOptimizationTime = Time.time;
        }
    }
    
    void OptimizeRenderers()
    {
        Vector3 cameraPosition = xrCamera.transform.position;
        float currentFrameTime = Time.deltaTime * 1000f;
        
        // Determine optimization level based on performance
        OptimizationLevel targetLevel = GetOptimizationLevel(currentFrameTime);
        
        foreach (var renderer in managedRenderers)
        {
            if (renderer == null) continue;
            
            float distance = Vector3.Distance(cameraPosition, renderer.transform.position);
            
            if (distance <= optimizationRadius)
            {
                renderer.SetOptimizationLevel(targetLevel, distance);
            }
            else
            {
                renderer.SetOptimizationLevel(OptimizationLevel.Maximum, distance);
            }
        }
    }
    
    OptimizationLevel GetOptimizationLevel(float frameTime)
    {
        if (frameTime <= highPerformanceThreshold)
        {
            return OptimizationLevel.None;
        }
        else if (frameTime <= lowPerformanceThreshold)
        {
            return OptimizationLevel.Medium;
        }
        else
        {
            return OptimizationLevel.Maximum;
        }
    }
}

public enum OptimizationLevel
{
    None,
    Medium,
    Maximum
}

public class OptimizedRenderer : MonoBehaviour
{
    [Header("Optimization Materials")]
    public Material originalMaterial;
    public Material optimizedMaterial;
    public Material ultraOptimizedMaterial;
    
    private Renderer objectRenderer;
    private OptimizationLevel currentLevel = OptimizationLevel.None;
    
    void Start()
    {
        objectRenderer = GetComponent<Renderer>();
        
        if (originalMaterial == null && objectRenderer != null)
        {
            originalMaterial = objectRenderer.material;
        }
    }
    
    public void SetOptimizationLevel(OptimizationLevel level, float distance)
    {
        if (level == currentLevel) return;
        
        currentLevel = level;
        
        if (objectRenderer == null) return;
        
        switch (level)
        {
            case OptimizationLevel.None:
                objectRenderer.material = originalMaterial;
                SetShaderLOD(300);
                break;
                
            case OptimizationLevel.Medium:
                if (optimizedMaterial != null)
                {
                    objectRenderer.material = optimizedMaterial;
                }
                SetShaderLOD(200);
                break;
                
            case OptimizationLevel.Maximum:
                if (ultraOptimizedMaterial != null)
                {
                    objectRenderer.material = ultraOptimizedMaterial;
                }
                else if (optimizedMaterial != null)
                {
                    objectRenderer.material = optimizedMaterial;
                }
                SetShaderLOD(100);
                break;
        }
        
        // Update shader properties based on distance
        UpdateShaderProperties(distance);
    }
    
    void SetShaderLOD(int lodLevel)
    {
        if (objectRenderer?.material != null)
        {
            objectRenderer.material.shader.maximumLOD = lodLevel;
        }
    }
    
    void UpdateShaderProperties(float distance)
    {
        if (objectRenderer?.material == null) return;
        
        // Fade LOD based on distance
        float lodFade = Mathf.Clamp01(1f - (distance / 50f));
        objectRenderer.material.SetFloat("_LODFade", lodFade);
        
        // Enable simple lighting for distant objects
        bool useSimpleLighting = distance > 25f;
        objectRenderer.material.SetFloat("_UseSimpleLighting", useSimpleLighting ? 1f : 0f);
    }
}
\`\`\`
      `,
      learningObjectives: [
        'Monitor and optimize XR performance',
        'Implement adaptive LOD systems',
        'Create XR-optimized shaders',
        'Build dynamic quality adjustment systems'
      ]
    }
  ]
};
